import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import AdminLayout from '../../components/layout/AdminLayout';
import VenturesPanel from './VenturesPanel';
import {
  Compass, Briefcase, HeartHandshake, ArrowRight, Sparkles, RefreshCw, CheckCircle2,
  FileText, Rocket, ClipboardCheck, Circle, ChevronDown, UserPlus, User, Lightbulb, Mail,
  Activity, Users, Inbox, MessageSquarePlus,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: API_URL, withCredentials: true });

const Ring = ({ pct, color = '#1D4ED8', size = 52 }) => {
  const r = 20, c = 2 * Math.PI * r;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg viewBox="0 0 48 48" className="w-full h-full -rotate-90">
        <circle cx="24" cy="24" r={r} fill="none" stroke="#EEF2F7" strokeWidth="5" />
        <circle cx="24" cy="24" r={r} fill="none" stroke={color} strokeWidth="5" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c - (c * pct) / 100} className="transition-all duration-700" />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center text-[11px] font-black text-[#0A0F1C]">{pct}%</div>
    </div>
  );
};

export default function ClarityHub() {
  const [people, setPeople] = useState([]);
  const [selected, setSelected] = useState(null); // {key,name,email,...}
  const [pickerOpen, setPickerOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [launchingId, setLaunchingId] = useState(null);
  const [insight, setInsight] = useState('');
  const [inviting, setInviting] = useState(false);
  const [tab, setTab] = useState('overview');
  const [pipeline, setPipeline] = useState(null);
  const [autoGen, setAutoGen] = useState(false);
  const [savingSetting, setSavingSetting] = useState(false);
  const [unlockPrice, setUnlockPrice] = useState(49);
  const [priceDraft, setPriceDraft] = useState('49');
  const [nudgeAuto, setNudgeAuto] = useState(false);
  const [nudgeCandidates, setNudgeCandidates] = useState(0);
  const [sendingNudges, setSendingNudges] = useState(false);
  const navigate = useNavigate();

  const loadPeople = useCallback(async () => {
    try {
      const r = await api.get('/api/admin/clarity-hub/people');
      setPeople(r.data.people || []);
      setSelected((cur) => cur || (r.data.people || [])[0] || null);
    } catch (e) { toast.error('Could not load people: ' + (e.response?.data?.detail || e.message)); }
  }, []);

  const loadHub = useCallback(async (personKey) => {
    setLoading(true);
    setInsight('');
    try {
      const r = await api.get(`/api/admin/clarity-hub?person=${encodeURIComponent(personKey)}`);
      setData(r.data);
      const hasData = (r.data.business?.total || 0) > 0 || (r.data.nonprofit?.projects?.length || 0) > 0;
      if (hasData) {
        api.get(`/api/admin/clarity-hub/insight?person=${encodeURIComponent(personKey)}`)
          .then((ir) => setInsight(ir.data.insight || '')).catch(() => {});
      }
    } catch (e) { toast.error('Could not load Clarity Hub: ' + (e.response?.data?.detail || e.message)); }
    finally { setLoading(false); }
  }, []);

  const invitePerson = async () => {
    if (!selected || selected.key === 'self') return;
    if (!selected.email) { toast.error('This person needs an email to be invited'); return; }
    setInviting(true);
    try {
      const r = await api.post('/api/admin/clarity-hub/invite', { name: selected.name, email: selected.email });
      toast.success(r.data.emailed ? `Invite emailed to ${selected.email}` : 'Invite created (email not sent — check email settings)');
    } catch (e) { toast.error('Invite failed: ' + (e.response?.data?.detail || e.message)); }
    finally { setInviting(false); }
  };

  useEffect(() => { loadPeople(); }, [loadPeople]);
  useEffect(() => { if (selected) loadHub(selected.key); }, [selected, loadHub]);

  useEffect(() => {
    api.get('/api/admin/clarity-hub/pipeline').then((r) => setPipeline(r.data)).catch(() => {});
    api.get('/api/admin/clarity-hub/settings').then((r) => {
      setAutoGen(!!r.data.client_autogenerate);
      setNudgeAuto(!!r.data.nudge_auto);
      setUnlockPrice(r.data.blueprint_unlock_price ?? 49);
      setPriceDraft(String(r.data.blueprint_unlock_price ?? 49));
    }).catch(() => {});
    api.get('/api/admin/clarity-hub/nudges').then((r) => setNudgeCandidates(r.data.candidates || 0)).catch(() => {});
  }, []);

  const toggleAutoGen = async () => {
    const next = !autoGen;
    setSavingSetting(true);
    try {
      await api.patch('/api/admin/clarity-hub/settings', { client_autogenerate: next });
      setAutoGen(next);
      toast.success(next ? 'Client blueprints will auto-generate on submit' : 'You will review client submissions before building');
    } catch (e) { toast.error('Could not save: ' + (e.response?.data?.detail || e.message)); }
    finally { setSavingSetting(false); }
  };

  const toggleNudgeAuto = async () => {
    const next = !nudgeAuto;
    try {
      await api.patch('/api/admin/clarity-hub/settings', { nudge_auto: next });
      setNudgeAuto(next);
      toast.success(next ? 'Daily reminder emails are ON' : 'Automatic reminders are OFF');
    } catch (e) { toast.error('Could not save: ' + (e.response?.data?.detail || e.message)); }
  };

  const savePrice = async () => {
    const val = parseFloat(priceDraft);
    if (isNaN(val) || val < 0) { toast.error('Enter a valid price'); return; }
    try {
      const r = await api.patch('/api/admin/clarity-hub/settings', { blueprint_unlock_price: val });
      setUnlockPrice(r.data.blueprint_unlock_price);
      setPriceDraft(String(r.data.blueprint_unlock_price));
      toast.success(`Unlock price set to $${r.data.blueprint_unlock_price}`);
    } catch (e) { toast.error('Could not save: ' + (e.response?.data?.detail || e.message)); }
  };

  const sendNudges = async () => {
    setSendingNudges(true);
    try {
      const r = await api.post('/api/admin/clarity-hub/nudges/send');
      toast.success(r.data.sent ? `Sent ${r.data.sent} reminder${r.data.sent > 1 ? 's' : ''}` : 'No unfinished interviews to remind right now');
      api.get('/api/admin/clarity-hub/nudges').then((res) => setNudgeCandidates(res.data.candidates || 0)).catch(() => {});
    } catch (e) { toast.error('Could not send: ' + (e.response?.data?.detail || e.message)); }
    finally { setSendingNudges(false); }
  };

  const startBusiness = (p) => {
    if (!p || p.key === 'self') { navigate('/admin/jumpstart-sessions'); return; }
    const q = new URLSearchParams({ new: '1', client_name: p.name || '', client_email: p.email || '' });
    navigate(`/admin/jumpstart-sessions?${q.toString()}`);
  };

  const launchNonprofit = async (sessionId) => {
    setLaunchingId(sessionId);
    try {
      const r = await api.post(`/api/admin/clarity-hub/launch-nonprofit/${sessionId}`);
      toast.success(`Created "${r.data.name}" — opening your Accelerator`);
      navigate('/admin/nonprofit-accelerator');
    } catch (e) {
      toast.error('Launch failed: ' + (e.response?.data?.detail || e.message));
      setLaunchingId(null);
    }
  };

  const onRec = (rec) => {
    if (rec.action === 'launch_nonprofit' && rec.session_id) launchNonprofit(rec.session_id);
    else if (rec.action === 'start_business') startBusiness(selected);
    else if (rec.to) navigate(rec.to);
  };

  const addPerson = () => {
    const name = newName.trim();
    if (!name) { toast.error('Enter a name'); return; }
    const email = newEmail.trim().toLowerCase();
    const p = { key: email || `name::${name.toLowerCase()}`, name, email, source: 'new' };
    setSelected(p);
    setAdding(false); setPickerOpen(false); setNewName(''); setNewEmail('');
    toast.success(`Now strategizing with ${name} — start their Business Jumpstart to begin`);
  };

  const biz = data?.business;
  const np = data?.nonprofit;
  const isSelf = selected?.key === 'self';
  const label = isSelf ? 'you' : (selected?.name || 'this person');

  return (
    <AdminLayout title="Clarity Hub">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6" data-testid="clarity-hub-page">
        {/* Header + person picker */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-1">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#1D4ED8] to-fuchsia-500 flex items-center justify-center text-white"><Compass size={20} /></span>
            <div>
              <h1 className="text-2xl font-black text-[#0A0F1C] tracking-tight">Clarity Hub</h1>
              <p className="text-sm text-[#64748B]">Strategize someone's business and nonprofit in one place — with the next best step for both.</p>
            </div>
          </div>
          {/* Picker */}
          <div className="flex items-center gap-2">
            {selected && !isSelf && (
              <button type="button" onClick={invitePerson} disabled={inviting} data-testid="clarity-invite-btn" title="Email this person a link to answer their Jumpstart before your session" className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-xl border border-[#BFDBFE] text-[#1D4ED8] bg-[#EFF6FF] hover:bg-[#DBEAFE] text-sm font-bold transition-colors">
                {inviting ? <RefreshCw size={15} className="animate-spin" /> : <Mail size={15} />} Invite to self-serve
              </button>
            )}
            <div className="relative">
            <button type="button" onClick={() => setPickerOpen((v) => !v)} data-testid="clarity-person-picker" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#E2E8F0] bg-white hover:border-[#1D4ED8] transition-colors">
              <span className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-black ${isSelf ? 'bg-[#0A0F1C]' : 'bg-[#1D4ED8]'}`}>{isSelf ? 'ME' : (selected?.name || '?').slice(0, 1).toUpperCase()}</span>
              <span className="text-left">
                <span className="block text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] leading-none">Working with</span>
                <span className="block text-sm font-bold text-[#0A0F1C] leading-tight">{selected?.name || 'Select…'}</span>
              </span>
              <ChevronDown size={16} className={`text-[#94A3B8] transition-transform ${pickerOpen ? 'rotate-180' : ''}`} />
            </button>
            {pickerOpen && (
              <div className="absolute right-0 mt-2 w-72 bg-white border border-[#E2E8F0] rounded-xl shadow-lg z-20 overflow-hidden" data-testid="clarity-person-menu">
                <div className="max-h-72 overflow-y-auto py-1">
                  {people.map((p) => (
                    <button type="button" key={p.key} onClick={() => { setSelected(p); setPickerOpen(false); setAdding(false); }} data-testid={`clarity-person-${p.key}`} className={`w-full text-left px-3 py-2 flex items-center gap-2.5 hover:bg-slate-50 ${selected?.key === p.key ? 'bg-[#EFF6FF]' : ''}`}>
                      <span className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-[11px] font-black shrink-0 ${p.key === 'self' ? 'bg-[#0A0F1C]' : 'bg-[#1D4ED8]'}`}>{p.key === 'self' ? 'ME' : (p.name || '?').slice(0, 1).toUpperCase()}</span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-bold text-[#0A0F1C] truncate">{p.name}</span>
                        {p.email && <span className="block text-[11px] text-[#94A3B8] truncate">{p.email}</span>}
                      </span>
                      <span className="flex gap-1 shrink-0">
                        {p.has_business && <Briefcase size={12} className="text-[#1D4ED8]" />}
                        {p.has_nonprofit && <HeartHandshake size={12} className="text-fuchsia-600" />}
                      </span>
                    </button>
                  ))}
                </div>
                <div className="border-t border-[#F1F5F9] p-2">
                  {!adding ? (
                    <button type="button" onClick={() => setAdding(true)} data-testid="clarity-add-person" className="w-full inline-flex items-center gap-2 px-3 py-2 text-sm font-bold text-[#1D4ED8] hover:bg-[#EFF6FF] rounded-lg"><UserPlus size={15} /> Add someone new</button>
                  ) : (
                    <div className="p-1 space-y-2" data-testid="clarity-add-form">
                      <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Full name" data-testid="clarity-new-name" className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm focus:outline-none focus:border-[#1D4ED8]" />
                      <input value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="Email (recommended)" data-testid="clarity-new-email" className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm focus:outline-none focus:border-[#1D4ED8]" />
                      <div className="flex gap-2">
                        <button type="button" onClick={addPerson} data-testid="clarity-new-save" className="flex-1 px-3 py-2 text-sm font-bold bg-[#1D4ED8] text-white rounded-lg hover:bg-[#1E40AF]">Start with them</button>
                        <button type="button" onClick={() => setAdding(false)} className="px-3 py-2 text-sm font-bold text-[#64748B] hover:text-[#0A0F1C]">Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          </div>

        </div>

        {/* Pipeline Snapshot */}
        {pipeline && (
          <div className="mt-4 rounded-2xl border border-[#E2E8F0] bg-white overflow-hidden" data-testid="clarity-pipeline">
            <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-[#F1F5F9]">
              {[
                { key: 'in_progress', label: 'In progress', value: pipeline.in_progress, icon: Users, color: '#1D4ED8', sub: `${pipeline.in_progress === 1 ? 'person' : 'people'} working on clarity` },
                { key: 'blueprints_ready', label: 'Blueprints ready', value: pipeline.blueprints_ready, icon: FileText, color: '#059669', sub: 'strategic blueprints built' },
                { key: 'launching', label: 'Launching', value: pipeline.launching, icon: Rocket, color: '#C026D3', sub: 'nonprofits mid-launch' },
                { key: 'submitted_for_review', label: 'Awaiting review', value: pipeline.submitted_for_review, icon: Inbox, color: '#F97316', sub: 'client submissions to build' },
              ].map((m) => {
                const Icon = m.icon;
                return (
                  <div key={m.key} className="p-4 flex items-center gap-3" data-testid={`pipeline-${m.key}`}>
                    <span className="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center text-white" style={{ background: m.color }}><Icon size={18} /></span>
                    <div className="min-w-0">
                      <div className="text-2xl font-black text-[#0A0F1C] leading-none">{m.value}</div>
                      <div className="text-[11px] font-bold text-[#334155] mt-1">{m.label}</div>
                      <div className="text-[10px] text-[#94A3B8] truncate">{m.sub}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="border-t border-[#F1F5F9] px-4 py-3 flex flex-wrap items-center gap-x-5 gap-y-3 bg-slate-50" data-testid="clarity-settings-bar">
              <button type="button" onClick={() => navigate('/admin/combined-interview')} data-testid="clarity-open-combined" className="inline-flex items-center gap-1.5 text-xs font-bold text-[#1D4ED8] hover:underline">
                <MessageSquarePlus size={14} /> Combined interview
              </button>
              <label className="inline-flex items-center gap-2 text-xs font-semibold text-[#475569] cursor-pointer" data-testid="clarity-autogen-toggle">
                <input type="checkbox" checked={autoGen} disabled={savingSetting} onChange={toggleAutoGen} className="accent-[#1D4ED8]" />
                Auto-build on submit {savingSetting && <RefreshCw size={12} className="animate-spin" />}
              </label>
              <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#475569]" data-testid="clarity-unlock-price">
                <span>Unlock price $</span>
                <input type="number" min="0" step="1" value={priceDraft} onChange={(e) => setPriceDraft(e.target.value)} data-testid="clarity-price-input" className="w-16 px-2 py-1 border border-[#E2E8F0] rounded-md text-xs bg-white focus:outline-none focus:border-[#1D4ED8]" />
                {String(unlockPrice) !== priceDraft && <button type="button" onClick={savePrice} data-testid="clarity-price-save" className="text-[#1D4ED8] font-bold hover:underline">Save</button>}
              </div>
              <div className="inline-flex items-center gap-3 ml-auto">
                <label className="inline-flex items-center gap-2 text-xs font-semibold text-[#475569] cursor-pointer" data-testid="clarity-nudge-toggle">
                  <input type="checkbox" checked={nudgeAuto} onChange={toggleNudgeAuto} className="accent-[#1D4ED8]" />
                  Auto reminders
                </label>
                <button type="button" onClick={sendNudges} disabled={sendingNudges} data-testid="clarity-send-nudges" className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg border border-[#E2E8F0] bg-white text-[#334155] hover:border-[#F97316] hover:text-[#F97316] disabled:opacity-60">
                  {sendingNudges ? <RefreshCw size={12} className="animate-spin" /> : <Mail size={13} />} Send reminders{nudgeCandidates ? ` (${nudgeCandidates})` : ''}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="mt-4 flex gap-1 border-b border-[#E2E8F0]">
          {[['overview', 'Overview'], ['essentials', 'Business Essentials']].map(([k, lbl]) => (
            <button type="button" key={k} onClick={() => setTab(k)} data-testid={`clarity-tab-${k}`} className={`px-4 py-2 text-sm font-bold -mb-px border-b-2 transition-colors ${tab === k ? 'border-[#1D4ED8] text-[#1D4ED8]' : 'border-transparent text-[#64748B] hover:text-[#0A0F1C]'}`}>{lbl}</button>
          ))}
        </div>

        {tab === 'essentials' ? (
          <div className="mt-5" data-testid="clarity-essentials">
            {selected && <VenturesPanel base="/api/admin/clarity-hub" q={`?person=${encodeURIComponent(selected.key)}`} allowDelete />}
          </div>
        ) : loading ? (
          <div className="mt-10 text-center text-sm text-[#64748B]" data-testid="clarity-loading"><RefreshCw className="animate-spin inline mr-2" size={18} /> Loading…</div>
        ) : !data ? null : (
          <>
            {/* Recommended */}
            <div className="mt-5 space-y-3" data-testid="clarity-recommended">
              {data.recommended.length === 0 ? (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm font-bold text-emerald-700 flex items-center gap-2"><CheckCircle2 size={16} /> {isSelf ? "You're" : `${label} is`} on track across both tracks — keep it up!</div>
              ) : data.recommended.map((rec, i) => (
                <button key={i} type="button" onClick={() => onRec(rec)} disabled={launchingId === rec.session_id} data-testid={`clarity-rec-${i}`}
                  className={`w-full text-left rounded-2xl border p-4 sm:p-5 flex items-center gap-4 transition-all hover:-translate-y-0.5 hover:shadow-md ${rec.tone === 'business' ? 'border-[#BFDBFE] bg-gradient-to-r from-[#EFF6FF] to-white' : 'border-fuchsia-200 bg-gradient-to-r from-fuchsia-50 to-white'}`}>
                  <span className={`shrink-0 w-11 h-11 rounded-xl flex items-center justify-center text-white ${rec.tone === 'business' ? 'bg-[#1D4ED8]' : 'bg-fuchsia-600'}`}>{rec.tone === 'business' ? <Briefcase size={18} /> : <HeartHandshake size={18} />}</span>
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] font-black uppercase tracking-wider text-[#94A3B8]">Recommended next step</div>
                    <div className="text-base font-bold text-[#0A0F1C]">{rec.title}</div>
                    <p className="text-sm text-[#475569] mt-0.5">{rec.desc}</p>
                  </div>
                  <span className={`shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold text-white ${rec.tone === 'business' ? 'bg-[#1D4ED8]' : 'bg-fuchsia-600'}`}>
                    {launchingId === rec.session_id ? <><RefreshCw size={13} className="animate-spin" /> Working…</> : <>{rec.cta} <ArrowRight size={14} /></>}
                  </span>
                </button>
              ))}
            </div>

            {/* Hub Insight */}
            {insight && (
              <div className="mt-4 rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 to-white p-4 flex items-start gap-3" data-testid="clarity-insight">
                <span className="shrink-0 w-9 h-9 rounded-xl bg-amber-400 text-white flex items-center justify-center"><Lightbulb size={18} /></span>
                <div>
                  <div className="text-[10px] font-black uppercase tracking-wider text-amber-600">Why these connect</div>
                  <p className="text-sm text-[#334155] mt-0.5">{insight}</p>
                </div>
              </div>
            )}

            {/* Tracks */}
            <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-5">
              {/* Business */}
              <div className="rounded-2xl border border-[#E2E8F0] bg-white overflow-hidden" data-testid="clarity-business-track">
                <div className="h-1.5 bg-[#1D4ED8]" />
                <div className="p-5">
                  <div className="flex items-center justify-between">
                    <h2 className="text-sm font-black uppercase tracking-wider text-[#1D4ED8] flex items-center gap-2"><Briefcase size={16} /> Business Blueprint</h2>
                    <button type="button" onClick={() => startBusiness(selected)} data-testid="clarity-open-business" className="text-xs font-bold text-[#1D4ED8] hover:underline flex items-center gap-1">{biz.total ? 'Open' : 'Start'} <ArrowRight size={13} /></button>
                  </div>
                  <p className="text-xs text-[#64748B] mt-1">7-Stage Business Jumpstart — clarity on mission, offers & positioning. A nonprofit is still a business, so this is the foundation for both.</p>
                  <div className="mt-4 flex items-center gap-4">
                    <Ring pct={biz.total ? Math.round((biz.blueprints_ready / biz.total) * 100) : 0} />
                    <div>
                      <div className="text-2xl font-black text-[#0A0F1C]">{biz.blueprints_ready}<span className="text-base text-[#94A3B8]"> / {biz.total} blueprint{biz.total === 1 ? '' : 's'} ready</span></div>
                      <div className="text-xs text-[#64748B]">{biz.total === 0 ? 'No Jumpstart yet' : `${biz.total} session${biz.total === 1 ? '' : 's'}`}</div>
                    </div>
                  </div>
                  <div className="mt-4 space-y-2">
                    {biz.sessions.slice(0, 4).map((s) => (
                      <div key={s.session_id} data-testid={`clarity-biz-session-${s.session_id}`} className="flex items-center gap-2.5 text-sm border border-[#F1F5F9] rounded-lg px-3 py-2">
                        {s.has_deliverable ? <FileText size={15} className="text-[#1D4ED8] shrink-0" /> : <Circle size={15} className="text-slate-300 shrink-0" />}
                        <span className="truncate flex-1 text-[#334155]">{s.business_name || s.client_name || 'Untitled'}</span>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${s.has_deliverable ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-500'}`}>{s.has_deliverable ? 'Blueprint ready' : (s.status || 'in progress')}</span>
                      </div>
                    ))}
                    {biz.sessions.length === 0 && (
                      <button type="button" onClick={() => startBusiness(selected)} data-testid="clarity-start-business" className="w-full mt-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-bold bg-[#1D4ED8] text-white rounded-lg hover:bg-[#1E40AF] transition-colors"><Sparkles size={14} /> Start {isSelf ? 'a' : `${label}'s`} Business Jumpstart</button>
                    )}
                  </div>
                </div>
              </div>

              {/* Nonprofit */}
              <div className="rounded-2xl border border-[#E2E8F0] bg-white overflow-hidden" data-testid="clarity-nonprofit-track">
                <div className="h-1.5 bg-fuchsia-500" />
                <div className="p-5">
                  <div className="flex items-center justify-between">
                    <h2 className="text-sm font-black uppercase tracking-wider text-fuchsia-700 flex items-center gap-2"><HeartHandshake size={16} /> Nonprofit Launch</h2>
                    <button type="button" onClick={() => navigate('/admin/nonprofit-accelerator')} data-testid="clarity-open-nonprofit" className="text-xs font-bold text-fuchsia-700 hover:underline flex items-center gap-1">Open <ArrowRight size={13} /></button>
                  </div>
                  <p className="text-xs text-[#64748B] mt-1">90-day Launch Accelerator — from mission to incorporation and board-ready. Pull a blueprint straight into a launch.</p>
                  <div className="mt-4 space-y-3">
                    {np.projects.map((p) => {
                      const pct = Math.round((p.days_done / p.days_total) * 100);
                      return (
                        <div key={p.project_id} data-testid={`clarity-project-${p.project_id}`} className="border border-[#F1F5F9] rounded-xl p-3 flex items-center gap-4 hover:border-fuchsia-200 transition-colors cursor-pointer" onClick={() => navigate('/admin/nonprofit-accelerator')}>
                          <Ring pct={pct} color="#C026D3" />
                          <div className="min-w-0 flex-1">
                            <div className="font-bold text-[#0A0F1C] truncate">{p.name}</div>
                            <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-[#64748B] mt-0.5">
                              <span className="flex items-center gap-1"><Rocket size={11} /> {p.days_done}/90 days</span>
                              <span className="flex items-center gap-1"><CheckCircle2 size={11} /> Board-ready {p.readiness}%</span>
                              {p.incorp_total > 0 && <span className="flex items-center gap-1"><ClipboardCheck size={11} /> 501(c)(3) {p.incorp_done}/{p.incorp_total}</span>}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {np.sessions.filter((s) => s.has_deliverable && !s.linked_project_id).map((s) => (
                      <div key={s.session_id} data-testid={`clarity-np-blueprint-${s.session_id}`} className="border border-fuchsia-200 bg-fuchsia-50/50 rounded-xl p-3 flex items-center gap-3">
                        <FileText size={16} className="text-fuchsia-600 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-bold text-[#0A0F1C] truncate">{s.business_name || s.client_name || 'Nonprofit blueprint'}</div>
                          <div className="text-[11px] text-[#64748B]">Blueprint ready — launch it into a 90-day plan</div>
                        </div>
                        <button type="button" onClick={() => launchNonprofit(s.session_id)} disabled={launchingId === s.session_id} data-testid={`clarity-launch-${s.session_id}`} className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold bg-fuchsia-600 text-white rounded-lg hover:bg-fuchsia-700 disabled:opacity-60 transition-colors">
                          {launchingId === s.session_id ? <><RefreshCw size={13} className="animate-spin" /> Launching…</> : <><Rocket size={13} /> Launch</>}
                        </button>
                      </div>
                    ))}
                    {np.projects.length === 0 && np.sessions.length === 0 && (
                      <div className="text-sm text-[#64748B] border border-dashed border-[#E2E8F0] rounded-xl p-4 text-center flex items-center justify-center gap-2"><User size={14} /> No nonprofit yet for {label}. Finish a blueprint, then launch it here.</div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Connection */}
            <div className="mt-6 rounded-2xl border border-[#E2E8F0] bg-slate-50 p-5 flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left" data-testid="clarity-connection">
              <div className="flex items-center gap-2 shrink-0">
                <span className="w-9 h-9 rounded-lg bg-[#1D4ED8] text-white flex items-center justify-center"><Briefcase size={16} /></span>
                <ArrowRight size={18} className="text-[#94A3B8]" />
                <span className="w-9 h-9 rounded-lg bg-fuchsia-600 text-white flex items-center justify-center"><HeartHandshake size={16} /></span>
              </div>
              <p className="text-sm text-[#475569]">Clarity first, then launch: run the <strong>Business Jumpstart</strong> to sharpen the vision, then hand it straight into the <strong>Nonprofit Accelerator</strong> — one connected journey for {isSelf ? 'you' : label}.</p>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
